import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useQueryGetExamAssignmentAnalysisReport,
  type IAnalysisReportFilters,
} from "@/apis/exam_assignments";
import type {
  IStudentPerformance,
  IModuleAnalysis,
  IStudentModule,
} from "@/apis/exam_assignments";
import { Button } from "@/components/button";
import { FunnelIcon, MinusIcon, PlusIcon } from "@heroicons/react/16/solid";
import FilteringAnalysisModal, {
  type AnalysisFilterState,
} from "./components/FilteringAnalysisModal";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";
import { useQueryGetUserList } from "@/apis/users/getUserList";

const ExamAssignmentAnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isDevelopment = false; 

  // Filter state
  const [filters, setFilters] = useState<AnalysisFilterState>({
    filterType: "none",
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [chunkSize, setChunkSize] = useState<number>(0); // 0 = all (no split)

  // Convert filter state to API filters
  const getAPIFilters = (): IAnalysisReportFilters | undefined => {
    switch (filters.filterType) {
      case "exam_time_group":
        return filters.examTimeGroupId
          ? { exam_time_group_id: filters.examTimeGroupId }
          : undefined;
      case "current_group":
        return filters.currentGroupId
          ? { current_group_id: filters.currentGroupId }
          : undefined;
      case "specific_students":
        return filters.selectedUserIds?.length
          ? { user_ids: filters.selectedUserIds.join(",") }
          : undefined;
      default:
        return undefined;
    }
  };

  const {
    data: analysisReport,
    isLoading,
    error,
  } = useQueryGetExamAssignmentAnalysisReport(id || "", getAPIFilters());

  // Get group and user data for filter display
  const { data: groupsData } = useQueryGetGroupList({ limit: 100 });
  const { data: usersData } = useQueryGetUserList({
    page: 1,
    limit: 1000,
    role: "student",
  });

  // Filter handlers
  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (newFilters: AnalysisFilterState) => {
    setFilters(newFilters);
    setIsFilterModalOpen(false);
  };

  const handleRemoveFilter = () => {
    setFilters({ filterType: "none" });
  };

  // Get group name by ID
  const getGroupName = (groupId: string) => {
    const group = groupsData?.items?.find((g) => g.id === groupId);
    return group?.name || "Unknown Group";
  };

  // Get user names by IDs
  const getUserNames = (userIds: string[]) => {
    if (!usersData?.items) return "";
    const users = usersData.items.filter((user) => userIds.includes(user.id));
    if (users.length <= 3) {
      return users.map((u) => u.name).join(", ");
    }
    return `${users
      .slice(0, 3)
      .map((u) => u.name)
      .join(", ")} and ${users.length - 3} more`;
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    switch (filters.filterType) {
      case "exam_time_group":
        if (filters.examTimeGroupId) {
          activeFilters.push({
            key: "exam_time_group",
            label: `Exam Time Group: ${getGroupName(filters.examTimeGroupId)}`,
            value: filters.examTimeGroupId,
          });
        }
        break;
      case "current_group":
        if (filters.currentGroupId) {
          activeFilters.push({
            key: "current_group",
            label: `Current Group: ${getGroupName(filters.currentGroupId)}`,
            value: filters.currentGroupId,
          });
        }
        break;
      case "specific_students":
        if (filters.selectedUserIds?.length) {
          activeFilters.push({
            key: "specific_students",
            label: `Students: ${getUserNames(filters.selectedUserIds)}`,
            value: filters.selectedUserIds.join(","),
          });
        }
        break;
    }

    return activeFilters;
  };

  // 과목별 모듈 데이터를 정리하는 함수
  const getModulesBySubject = (subject: string) => {
    if (
      !analysisReport ||
      !analysisReport.module_analyses ||
      !analysisReport.student_performances
    )
      return [];

    return analysisReport.module_analyses
      .filter((m: any) => m.subject === subject)
      .map((moduleAnalysis: any) => {
        const studentsForModule = analysisReport.student_performances
          .map((student) => {
            const foundModule = student.modules?.find(
              (m) =>
                m.module_no === moduleAnalysis.module_no &&
                m.subject === subject
            );
            return {
              progress_id: student.progress_id,
              user: student.user,
              total_score: student.total_score,
              subject_scores: student.subject_scores,
              module: foundModule,
            };
          })
          .filter((student) => student.module)
          .sort((a, b) => {
            if (analysisReport.exam_type === "AP") {
              const aCorrect = a.module?.correct_answers || 0;
              const bCorrect = b.module?.correct_answers || 0;
              return bCorrect - aCorrect;
            }
            const aScore = (a.subject_scores as any)?.[subject] || a.total_score;
            const bScore = (b.subject_scores as any)?.[subject] || b.total_score;
            return bScore - aScore;
          });

        return { moduleAnalysis, studentsForModule };
      });
  };

  // Set default chunk size based on exam type
  React.useEffect(() => {
    if (analysisReport?.exam_type === "AP") {
      setChunkSize(50);
    }
  }, [analysisReport?.exam_type]);

  // API 응답에서 고유 subject 목록을 동적으로 추출
  const subjects = useMemo(() => {
    if (!analysisReport?.module_analyses) return [];
    return [...new Set(analysisReport.module_analyses.map((m: any) => m.subject as string))];
  }, [analysisReport]);

  // Subject 표시명 헬퍼
  const getSubjectDisplayName = (subject: string): string => {
    const names: Record<string, string> = {
      reading_writing: "Reading & Writing",
      math: "Math",
      english: "English",
      reading: "Reading",
      science: "Science",
    };
    return names[subject] || subject.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // 문제별 정답률 계산 함수
  const calculateQuestionAccuracy = (
    questionId: string,
    studentsForModule: Array<{
      user: { id: string; name: string; email: string };
      total_score: number;
      module: IStudentModule | undefined;
    }>
  ) => {
    if (!studentsForModule || studentsForModule.length === 0) return 0;

    const totalAnswers = studentsForModule.length;
    const correctAnswers = studentsForModule.filter((student) =>
      student.module?.questions?.find(
        (q) => q.question_id === questionId && q.is_correct
      )
    ).length;

    return totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0;
  };

  // 정답률에 따른 배경색 계산 함수
  const getAccuracyBackgroundColor = (accuracy: number) => {
    if (accuracy <= 50) {
      // 0% ~ 50%: 투명한 빨간색에서 흰색으로
      const intensity = (50 - accuracy) / 50; // 1 (투명한 빨간색) ~ 0 (흰색)
      const red = 255;
      const green = Math.floor(255 - 155 * intensity); // 255에서 100으로
      const blue = Math.floor(255 - 155 * intensity); // 255에서 100으로
      return `rgb(${red}, ${green}, ${blue})`;
    } else {
      // 50% ~ 100%: 흰색에서 짙은 초록색으로
      const intensity = (accuracy - 50) / 50; // 0 (흰색) ~ 1 (짙은 초록색)
      const red = Math.floor(255 - 255 * intensity);
      const green = Math.floor(255 - 85 * intensity); // 255에서 170으로
      const blue = Math.floor(255 - 255 * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  const CHUNK_OPTIONS = [
    { label: "All", value: 0 },
    ...Array.from({ length: 10 }, (_, i) => ({
      label: String((i + 1) * 10),
      value: (i + 1) * 10,
    })),
  ];

  // 과목별 섹션 컴포넌트
  const SubjectSection = ({
    subject,
  }: {
    subject: string;
  }) => {
    const modulesData = getModulesBySubject(subject);

    if (!modulesData || modulesData.length === 0) return null;

    const sectionTitle = getSubjectDisplayName(subject);

    return (
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-4">{sectionTitle}</h3>

        {modulesData.map(({ moduleAnalysis, studentsForModule }, index: number) => {
          const allQuestions = moduleAnalysis.questions;
          const effectiveChunkSize = chunkSize;
          const questionChunks = effectiveChunkSize > 0
            ? Array.from(
                { length: Math.ceil(allQuestions.length / effectiveChunkSize) },
                (_, i) => allQuestions.slice(i * effectiveChunkSize, (i + 1) * effectiveChunkSize)
              )
            : [allQuestions];

          return questionChunks.map((chunkQuestions: any[], chunkIndex: number) => {
            const questionColumnCount =
              effectiveChunkSize > 0 ? effectiveChunkSize : chunkQuestions.length;
            const placeholderColumnCount = Math.max(
              questionColumnCount - chunkQuestions.length,
              0
            );
            const placeholderColumns = Array.from(
              { length: placeholderColumnCount },
              (_, placeholderIndex) => placeholderIndex
            );

            return (
              <div key={`${moduleAnalysis.module_no}-chunk-${chunkIndex}`} className="mb-4">
                <div className="overflow-x-auto">
                  <table
                    className="w-full border-collapse border border-gray-400"
                    style={{ fontSize: "10px", tableLayout: "fixed" }}
                  >
                    <colgroup>
                      <col style={{ width: "36px" }} />
                      <col style={{ width: "48px" }} />
                      <col style={{ width: "72px" }} />
                      {Array.from({ length: questionColumnCount }, (_, columnIndex) => (
                        <col key={`question-column-${columnIndex}`} />
                      ))}
                    </colgroup>
                    {/* 헤더 */}
                    <thead>
                      <tr>
                        <th
                          className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                          rowSpan={3}
                        >
                          Score
                        </th>
                        <th
                          className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                          rowSpan={3}
                        >
                          Module{moduleAnalysis.module_no}
                        </th>
                        <th
                          className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                          rowSpan={3}
                        >
                          Name
                        </th>
                        {chunkQuestions.map((question: any, qIndex: number) => (
                          <th
                            key={question.question_id}
                            className="border border-gray-400 bg-gray-200 px-[2px] py-1 text-center font-medium"
                          >
                            Q{effectiveChunkSize > 0 ? chunkIndex * effectiveChunkSize + qIndex + 1 : qIndex + 1}
                          </th>
                        ))}
                        {placeholderColumns.map((placeholderIndex) => (
                          <th
                            key={`empty-question-${placeholderIndex}`}
                            className="border border-gray-400 bg-gray-100 px-[2px] py-1 text-center font-medium"
                          />
                        ))}
                      </tr>
                      <tr>
                        {chunkQuestions.map((question: any) => (
                          <th
                            key={question.question_id}
                            className="border border-gray-400 px-[2px] py-1 text-center font-medium"
                            style={{ fontSize: "9px" }}
                          >
                            {question.correct_answer}
                          </th>
                        ))}
                        {placeholderColumns.map((placeholderIndex) => (
                          <th
                            key={`empty-answer-${placeholderIndex}`}
                            className="border border-gray-400 px-[2px] py-1 text-center font-medium"
                            style={{ fontSize: "9px" }}
                          />
                        ))}
                      </tr>
                      <tr>
                        {chunkQuestions.map((question: any) => {
                          const accuracy = calculateQuestionAccuracy(
                            question.question_id,
                            studentsForModule
                          );
                          return (
                            <th
                              key={question.question_id}
                              className="border border-gray-400 py-1 text-center font-medium"
                              style={{
                                fontSize: "9px",
                                backgroundColor:
                                  getAccuracyBackgroundColor(accuracy),
                              }}
                            >
                              {accuracy.toFixed(0)}%
                            </th>
                          );
                        })}
                        {placeholderColumns.map((placeholderIndex) => (
                          <th
                            key={`empty-accuracy-${placeholderIndex}`}
                            className="border border-gray-400 py-1 text-center font-medium"
                            style={{ fontSize: "9px" }}
                          />
                        ))}
                      </tr>
                    </thead>

                    {/* 학생별 답안 */}
                    <tbody>
                      {studentsForModule.map((student: any, sIndex: number) => (
                        <tr key={student.progress_id || `${student.user.id}-${sIndex}`}>
                          <td className="border border-gray-400 px-[2px] py-[2px] text-center font-medium">
                            {(student.subject_scores as any)?.[subject] ||
                              student.total_score}
                          </td>
                          <td className="border border-gray-400 px-[2px] py-[2px] text-center font-medium">
                            {student.module?.correct_answers || 0}/
                            {student.module?.total_questions || 0}
                          </td>
                          <td className="border border-gray-400 px-[2px] py-[2px] whitespace-nowrap text-[11px] text-center">
                            {student.user.name}
                          </td>
                          {chunkQuestions.map((question: any) => {
                            const studentAnswer = student.module?.questions.find(
                              (q: any) => q.question_id === question.question_id
                            );
                            const isCorrect = studentAnswer?.is_correct || false;
                            const userAnswer = studentAnswer?.user_answer || "";

                            return (
                              <td
                                key={question.question_id}
                                className={`border border-gray-400 px-0 py-[2px] text-center font-medium ${
                                  isCorrect ? "text-black" : "bg-red-200 text-black"
                                }`}
                              >
                                {userAnswer}
                              </td>
                            );
                          })}
                          {placeholderColumns.map((placeholderIndex) => (
                            <td
                              key={`empty-student-${placeholderIndex}`}
                              className="border border-gray-400 px-0 py-[2px] text-center font-medium"
                            />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          });
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading analysis report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">
          Error loading analysis report. Please try again.
        </div>
      </div>
    );
  }

  if (!analysisReport) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">No analysis report found.</div>
      </div>
    );
  }

  return (
    <>
      {/* Print CSS to hide filter elements */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* 과목별 페이지 — 동적으로 subject 반복, 첫 페이지에 필터 헤더 */}
      {subjects.map((subject, index) => (
        <div
          key={subject}
          className={`page w-[297mm] h-auto min-h-[210mm] px-6 py-4 text-black font-sans text-base bg-white overflow-hidden ${
            isDevelopment ? "border-2 border-red-500" : ""
          }`}
          style={{ pageBreakAfter: index < subjects.length - 1 ? "always" : "auto" }}
        >
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-bold">
                {analysisReport.exam_set_title}
              </h1>
              <div className="flex items-center gap-2 no-print">
                {index === 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 border border-gray-300 rounded px-1 py-0.5">
                      <span className="text-xs text-gray-500 px-1">Split</span>
                      <select
                        value={chunkSize}
                        onChange={(e) => setChunkSize(Number(e.target.value))}
                        className="text-xs border-none bg-transparent font-medium focus:outline-none cursor-pointer"
                      >
                        {CHUNK_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1 border border-gray-300 rounded px-1 py-0.5">
                      <button
                        onClick={() => setZoomLevel((z) => Math.max(50, z - 10))}
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={zoomLevel <= 50}
                      >
                        <MinusIcon className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs font-medium w-8 text-center">{zoomLevel}%</span>
                      <button
                        onClick={() => setZoomLevel((z) => Math.min(150, z + 10))}
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={zoomLevel >= 150}
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                {index === 0 && (
                  <Button onClick={handleOpenFilterModal}>
                    <FunnelIcon className="w-4 h-4" />
                    Filter
                  </Button>
                )}
              </div>
            </div>
            {index === 0 && (
              <div className="no-print">
                <ActiveFilters
                  filters={getActiveFilters()}
                  onRemoveFilter={handleRemoveFilter}
                  onClearAll={handleRemoveFilter}
                />
              </div>
            )}
          </div>

          <div style={{ zoom: zoomLevel / 100 }}>
            <SubjectSection subject={subject} />
          </div>
        </div>
      ))}

      {/* Filter Modal */}
      <FilteringAnalysisModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        onApply={handleApplyFilters}
        currentFilters={filters}
        examAssignmentId={id}
      />
    </>
  );
};

export default ExamAssignmentAnalysisPage;
